import { useState, useEffect, useContext, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { adminMagicVerify, getAdminProfile, validateToken } from '../service';
import { ProfileContext } from '../contexts/profileContext';
import Button from '../components/common/button';

function Magic() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [_, setProfileData] = useContext(ProfileContext);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [tokenDetails, setTokenDetails] = useState(null);
  const [countdown, setCountdown] = useState(7);

  const token = searchParams.get('ref');

  // Actually log in and redirect
  const handleVerification = useCallback(
    async (token) => {
      try {
        setIsLoading(true);
        setError(null);
        const { data: { token: verifiedToken = '' } = {} } = await adminMagicVerify({ token });
        if (!verifiedToken) throw new Error('Invalid token');
        localStorage.setItem('adminToken', verifiedToken);

        // Change language if present
        const lang = searchParams.get('lang');
        if (lang) {
          localStorage.setItem('lang', lang);
          i18n.changeLanguage(lang);
        }

        // Fetch user and set profile context
        const { data = {} } = await getAdminProfile();
        setProfileData({ ...data, loaded: true });
        navigate('/dashboard');
      } catch (e) {
        console.error('Verification Error:', e);
        navigate('/login');
      } finally {
        setIsLoading(false);
      }
    },
    [searchParams, i18n, setProfileData, navigate]
  );

  const handleValidation = async (token) => {
    try {
      const { data: { admin } = {} } = await validateToken({ token });
      setTokenDetails(admin);
    } catch (e) {
      console.error('Validation Error:', e);
      navigate('/login');
    }
  };

  useEffect(() => {
    if (!token) {
      navigate('/login');
      return;
    }
    handleValidation(token);
    // eslint-disable-next-line
  }, [searchParams]);

  // Countdown timer effect
  useEffect(() => {
    if (!tokenDetails || isLoading) return;

    if (countdown > 0) {
      const timer = setTimeout(() => {
        setCountdown(countdown - 1);
      }, 1000);
      return () => clearTimeout(timer);
    } else if (countdown === 0 && tokenDetails && !isLoading) {
      // Auto-login when countdown reaches 0
      // handleVerification(token);
    }
    // eslint-disable-next-line
  }, [countdown, tokenDetails, isLoading]);

  if (!token) {
    return null;
  }

  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="bg-white p-8 rounded-lg shadow-md max-w-md w-full">
        <h2 className="text-2xl font-bold mb-4 text-center">
          {tokenDetails?.firstName} {tokenDetails?.lastName} {t('magic.confirmLogin')}
        </h2>
        <p className="text-gray-600 mb-6 text-center">{t('magic.confirmLoginDescription')}</p>
        {countdown > 0 && (
          <div className="text-center mb-4">
            <p className="text-sm text-gray-500">
              {t('magic.autoLoginIn')} {countdown}{' '}
              {countdown === 1 ? t('magic.second') : t('magic.seconds')}
            </p>
          </div>
        )}
        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
            {error}
          </div>
        )}
        <Button.ButtonRounded
          onClick={() => handleVerification(token)}
          disabled={isLoading}
          className={`bg-gray-500 uppercase rounded text-white px-[78px] py-[14px] font-bold mx-auto disabled:opacity-60 gap-10 rounded-5`}>
          {isLoading ? (
            <div className="flex items-center justify-center">
              <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-white mr-2"></div>
              {t('magic.verifying')}
            </div>
          ) : (
            t('magic.confirmLogin')
          )}
        </Button.ButtonRounded>
      </div>
    </div>
  );
}

export default Magic;
